/**
 * QuestManager.mjs
 * Central state manager for Pattern Quest v2.
 *
 * Manages 3 always-active quests (light / creative / special),
 * progress tracking, reward application, and persistence.
 */

import { generateQuest } from './QuestGenerator.mjs';
import { updateQuestProgress, isComplete } from './QuestProgress.mjs';

const SAVE_KEY = 'hexaworlds_quests_v2';
const HISTORY_LIMIT = 50;

const TIERS = ['light', 'creative', 'special'];

export class QuestManager {
  constructor() {
    /** @type {{ active: {light,creative,special}, history: Array, totalCompletions: number }} */
    this.state = {
      active: { light: null, creative: null, special: null },
      history: [],
      totalCompletions: 0,
    };

    // ── Callbacks (set by caller in index.mjs) ──────────────────────
    /** (n: number) => void  — add n stars to score */
    this.onStarsReward = null;
    /** () => string|null  — unlock from regular overlay pool, returns key or null */
    this.onRegularOverlayReward = null;
    /** () => string|null  — unlock from special overlay pool, returns key or null */
    this.onSpecialOverlayReward = null;
    /** (tier: string, quest: object) => void — fired when quest completes */
    this.onQuestComplete = null;
    /** (tier: string, quest: object) => void — fired when progress changes */
    this.onProgress = null;
    /** (tier: string, quest: object) => void — fired when a new quest is set */
    this.onQuestNew = null;
    /** () => worldInfo — must be set by caller */
    this.getWorldInfo = null;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /** Call after construction to load save and generate initial quests. */
  init() {
    this._load();
    const wi = this._getWI();
    for (const tier of TIERS) {
      if (!this.state.active[tier]) {
        this.state.active[tier] = generateQuest(tier, wi, this.state.history);
        this._notify('onQuestNew', tier);
      }
    }
    this._save();
  }

  /**
   * Call when a tile is placed.
   * @param {{coord:{q,r}, matchCount:number, majorType:string, isOcean:boolean, neighbourCount:number, neighbourTerrainTypes:Set}} ev
   */
  onTilePlaced(ev) {
    this._processEvent({ type: 'tile', ...ev });
  }

  /**
   * Call when an overlay is placed.
   * @param {{coord:{q,r}, overlayFamily:string, adjacentOverlays:number, onBonusTerrain:boolean, isOcean:boolean}} ev
   */
  onOverlayPlaced(ev) {
    this._processEvent({ type: 'overlay', ...ev });
  }

  /** Called when ANY quest completes (for meta-quest S10). */
  notifyQuestCompletion() {
    const special = this.state.active.special;
    if (special?.category === 'meta' && !isComplete(special.progress)) {
      special.progress.current++;
      this._notify('onProgress', 'special');
      if (isComplete(special.progress)) {
        this._completeAndReplace('special');
      }
    }
  }

  // ── Internal ────────────────────────────────────────────────────────────────

  _processEvent(ev) {
    const wi = this._getWI();
    const worldFns = wi._fns;

    for (const tier of TIERS) {
      const quest = this.state.active[tier];
      if (!quest || isComplete(quest.progress)) continue;

      const wasComplete = updateQuestProgress(quest, ev, worldFns);
      this._notify('onProgress', tier);

      if (wasComplete) {
        this._completeAndReplace(tier);
      }
    }
    this._save();
  }

  _completeAndReplace(tier) {
    const quest = this.state.active[tier];
    if (!quest) return;

    quest.completedAtMs = Date.now();
    this.state.totalCompletions++;

    // Apply reward
    this._applyReward(quest);

    // Push to history
    this.state.history.push({
      signature: quest.signature,
      tier: quest.tier,
      category: quest.category,
      terrain: quest.params?.terrain || null,
      completedAtMs: quest.completedAtMs,
    });
    if (this.state.history.length > HISTORY_LIMIT) {
      this.state.history.shift();
    }

    // Fire complete callback
    this._notify('onQuestComplete', tier, quest);

    // Notify meta-quest if any
    const special = this.state.active.special;
    if (tier !== 'special' && special?.category === 'meta') {
      special.progress.current++;
      this._notify('onProgress', 'special');
      if (isComplete(special.progress)) {
        // defer to avoid re-entrancy
        setTimeout(() => this._completeAndReplace('special'), 0);
        return;
      }
    }

    // Generate replacement
    const wi = this._getWI();
    const newQuest = generateQuest(tier, wi, this.state.history);
    this.state.active[tier] = newQuest;
    this._notify('onQuestNew', tier);

    this._save();
  }

  _applyReward(quest) {
    const reward = quest.reward;
    if (reward.kind === 'stars') {
      if (this.onStarsReward) this.onStarsReward(reward.amount);
    } else if (reward.kind === 'overlay') {
      const fn = reward.pool === 'regular' ? this.onRegularOverlayReward : this.onSpecialOverlayReward;
      const picked = fn ? fn() : null;
      if (!picked) {
        // pool empty — fallback stars
        if (this.onStarsReward) this.onStarsReward(reward.fallbackStars);
      }
    }
  }

  _getWI() {
    if (!this.getWorldInfo) throw new Error('QuestManager.getWorldInfo not set');
    return this.getWorldInfo();
  }

  _notify(cbName, tier, quest) {
    const cb = this[cbName];
    if (!cb) return;
    try { cb(tier, quest || this.state.active[tier]); } catch (e) { console.warn('[QuestManager]', e); }
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  _save() {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify({
        active: this.state.active,
        history: this.state.history,
        totalCompletions: this.state.totalCompletions,
      }));
    } catch (_) {}
  }

  _load() {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.active) this.state.active = parsed.active;
      if (parsed.history) this.state.history = parsed.history;
      if (typeof parsed.totalCompletions === 'number') this.state.totalCompletions = parsed.totalCompletions;
    } catch (_) {
      // Corrupt save — start fresh
    }
  }

  /** Call on game reset */
  reset() {
    this.state = { active: { light: null, creative: null, special: null }, history: [], totalCompletions: 0 };
    try { localStorage.removeItem(SAVE_KEY); } catch (_) {}
    this.init();
  }
}
